// v49.5 — Cert kind catalogue + expiry-status helper.

export const CERT_KINDS: { id: string; l: string }[] = [
  { id: "cscs",    l: "CSCS card" },
  { id: "rtw",     l: "Right to Work" },
  { id: "18th",    l: "18th Edition (BS 7671)" },
  { id: "jib",     l: "JIB grading" },
  { id: "niceic",  l: "NICEIC certification" },
  { id: "compex",  l: "CompEx" },
  { id: "ipaf",    l: "IPAF (MEWP)" },
  { id: "pasma",   l: "PASMA (Towers)" },
  { id: "iso9001", l: "ISO 9001" },
  { id: "iso14001",l: "ISO 14001" },
  { id: "iso45001",l: "ISO 45001 / OHSAS" },
  { id: "ssip",    l: "SSIP (Achilles / CHAS / SafeContractor)" },
  { id: "insurance",l: "Public liability insurance" },
  { id: "other",   l: "Other" },
];

export const CERT_KIND_LABEL: Record<string, string> = Object.fromEntries(CERT_KINDS.map(c => [c.id, c.l]));

export function certLabel(id: string | null | undefined): string {
  if (!id) return "—";
  return CERT_KIND_LABEL[id] || id;
}

export type ExpiryStatus = "ok" | "warning" | "critical" | "expired" | "no-expiry";

export function expiryStatus(expiresAt: string | null | undefined): { status: ExpiryStatus; daysLeft: number | null } {
  if (!expiresAt) return { status: "no-expiry", daysLeft: null };
  try {
    const target = new Date(expiresAt).getTime();
    const now    = Date.now();
    const days   = Math.round((target - now) / 86400000);
    if (days < 0)   return { status: "expired",  daysLeft: days };
    if (days <= 7)  return { status: "critical", daysLeft: days };
    if (days <= 30) return { status: "warning",  daysLeft: days };
    return { status: "ok", daysLeft: days };
  } catch {
    return { status: "no-expiry", daysLeft: null };
  }
}

export function formatExpiry(expiresAt: string | null | undefined): string {
  if (!expiresAt) return "No expiry recorded";
  try { return new Date(expiresAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return "—"; }
}

/* v50.120 — AI verification status. Derived from ai_verified_at + ai_flags.
   "clean" → no mismatches, doc looks like what the user said it was.
   "mismatch" → one or more soft flags (low_confidence / no_expiry_found / expiry_mismatch).
   "blocked" → hard flags (wrong_doc_type / kind_mismatch / unclassifiable).
   "unverified" → /api/compliance-verify-doc never ran on this row. */
export type AiVerifyStatus = "unverified" | "clean" | "mismatch" | "blocked";

const HARD_FLAGS = new Set(["wrong_doc_type", "kind_mismatch", "unclassifiable"]);

export function aiVerifyStatus(doc: {
  ai_verified_at?: string | null;
  ai_flags?:       string[] | null;
}): AiVerifyStatus {
  if (!doc.ai_verified_at) return "unverified";
  const flags = doc.ai_flags || [];
  if (flags.length === 0) return "clean";
  if (flags.some(f => HARD_FLAGS.has(f))) return "blocked";
  return "mismatch";
}

/* v50.329 — Defence P3 verification layer (migration-142). The status is
   derived server-side from real evidence only: a human review (manual),
   a clean AI document check (ai_document), or — once a CSCS Smart Check /
   CHAS style integration lands — an issuing-body confirmation (issuer_api).
   We never label something verified without one of those. */
export const VERIFICATION_SOURCE_LABELS: Record<string, string> = {
  manual:      "Reviewed by LabourCore",
  ai_document: "Document verified (AI)",
  issuer_api:  "Confirmed with issuing body",
};

export function verificationBadge(doc: {
  verification_status?: string | null;
  verification_source?: string | null;
}): { label: string; tone: "green" | "amber" | "red" | "neutral" } | null {
  switch (doc.verification_status) {
    case "verified": return {
      label: VERIFICATION_SOURCE_LABELS[doc.verification_source || ""] || "Verified",
      tone: "green",
    };
    case "expired":  return { label: "Verification lapsed", tone: "red" };
    case "failed":   return { label: "Verification failed", tone: "red" };
    case "unverified": return { label: "Not yet verified", tone: "neutral" };
    default: return null;
  }
}

export const AI_FLAG_LABELS: Record<string, string> = {
  kind_mismatch:   "Detected cert kind doesn't match what you selected",
  expiry_mismatch: "Detected expiry doesn't match what you entered",
  no_expiry_found: "No expiry date could be read from the document",
  low_confidence:  "Model wasn't fully confident this is the right cert",
  unclassifiable:  "Document couldn't be classified as any known cert kind",
  wrong_doc_type:  "Document appears to be a different type entirely",
};
